const MAX_IMAGE_EDGE = 1600;
const AVATAR_EDGE = 512;
const OUTPUT_QUALITY = 0.72;
const AVATAR_QUALITY = 0.82;
const SKIP_TYPES = new Set(["image/gif", "image/svg+xml", "image/x-icon", "image/vnd.microsoft.icon"]);

function outputType() {
  if (typeof document === "undefined") {
    return "image/jpeg" as const;
  }
  const canvas = document.createElement("canvas");
  return canvas.toDataURL("image/webp").startsWith("data:image/webp")
    ? ("image/webp" as const)
    : ("image/jpeg" as const);
}

function extensionFor(type: string) {
  return type === "image/webp" ? "webp" : "jpg";
}

function replaceExtension(name: string, extension: string) {
  return name.replace(/\.[^.]+$/u, "") + `.${extension}`;
}

function scaledSize(width: number, height: number) {
  const longest = Math.max(width, height);
  if (longest <= MAX_IMAGE_EDGE) {
    return { width, height };
  }
  const ratio = MAX_IMAGE_EDGE / longest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

async function bitmapFromFile(file: File) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file, { imageOrientation: "from-image" });
  }

  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("A kép nem tölthető be."));
      element.src = url;
    });
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number) {
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

export async function optimizeImageFile(file: File): Promise<File> {
  if (!file.type.startsWith("image/") || SKIP_TYPES.has(file.type)) {
    return file;
  }

  try {
    const source = await bitmapFromFile(file);
    const { width, height } = scaledSize(source.width, source.height);
    const alreadySmall =
      width === source.width &&
      height === source.height &&
      file.size <= 280 * 1024;

    if (alreadySmall) {
      if ("close" in source && typeof source.close === "function") {
        source.close();
      }
      return file;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      return file;
    }

    context.drawImage(source, 0, 0, width, height);
    if ("close" in source && typeof source.close === "function") {
      source.close();
    }

    const type = outputType();
    const blob = await canvasToBlob(canvas, type, OUTPUT_QUALITY);
    if (!blob || blob.size >= file.size) {
      return file;
    }

    return new File([blob], replaceExtension(file.name, extensionFor(type)), {
      type,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}

export async function optimizeAvatarFile(file: File): Promise<File> {
  if (!file.type.startsWith("image/") || SKIP_TYPES.has(file.type)) {
    throw new Error("Csak JPG, PNG vagy WebP kép tölthető fel.");
  }

  let source: ImageBitmap | HTMLImageElement;
  try {
    source = await bitmapFromFile(file);
  } catch {
    throw new Error("A kép nem tölthető be. Használjon JPG, PNG vagy WebP fájlt.");
  }
  const size = Math.min(source.width, source.height);
  if (size < 32) {
    if ("close" in source && typeof source.close === "function") {
      source.close();
    }
    throw new Error("A profilkép legalább 32×32 pixel legyen.");
  }

  const sx = Math.floor((source.width - size) / 2);
  const sy = Math.floor((source.height - size) / 2);
  const edge = Math.min(AVATAR_EDGE, size);
  const canvas = document.createElement("canvas");
  canvas.width = edge;
  canvas.height = edge;
  const context = canvas.getContext("2d");
  if (!context) {
    if ("close" in source && typeof source.close === "function") {
      source.close();
    }
    throw new Error("A kép optimalizálása nem sikerült.");
  }

  context.fillStyle = "#fff";
  context.fillRect(0, 0, edge, edge);
  context.drawImage(source, sx, sy, size, size, 0, 0, edge, edge);
  if ("close" in source && typeof source.close === "function") {
    source.close();
  }

  const type = outputType();
  const blob = await canvasToBlob(canvas, type, AVATAR_QUALITY);
  if (!blob) {
    throw new Error("A kép optimalizálása nem sikerült.");
  }

  return new File([blob], replaceExtension(file.name, extensionFor(type)), {
    type,
    lastModified: Date.now(),
  });
}

export async function optimizeImageFiles(files: File[]) {
  return Promise.all(files.map((file) => optimizeImageFile(file)));
}
